import { inject, signal, Service } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { AuthResponse } from '../auth/interfaces/auth.interface';
import { API_PREFIX } from '../consts/general.constants';
import { map, Observable, switchMap, tap } from 'rxjs';
import { AUTH_API_ENDPOINTS } from '../auth/consts/auth.constants';
import { TokenService } from './token.service';
import { SignUpPayload } from '../../shared/interfaces/sign-up.inteerface';
import { OnboardingService } from './onboarding.service';
import {
  ResetPasswordRequest,
  ValidateNewPasswordResponse,
} from '../../shared/interfaces/password-reset.interface';

@Service()
export class AuthService {
  private readonly baseUrl = environment.apiBaseUrl;
  private readonly fullApiUrl = `${this.baseUrl}${API_PREFIX}`;

  private readonly http = inject(HttpClient);
  private router = inject(Router);
  private tokenService = inject(TokenService);
  private readonly onboardingService = inject(OnboardingService);

  public isAuthenticated = signal<boolean>(this.tokenService.hasToken());

  public signUp(signUpData: SignUpPayload): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.fullApiUrl}${AUTH_API_ENDPOINTS.signup}`, signUpData)
      .pipe(
        tap((response) => {
          this.tokenService.setTokens(response.data);
          this.isAuthenticated.set(true);
        }),
      );
  }

  public logout(): Observable<null> {
    return this.http
      .post<AuthResponse>(`${this.fullApiUrl}${AUTH_API_ENDPOINTS.logout}`, null)
      .pipe(
        tap(() => {
          this.tokenService.clearTokens();
          this.isAuthenticated.set(false);
          this.router.navigate(['/']);
        }),
        map(() => null),
      );
  }

  public login(email: string, password: string): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.fullApiUrl}${AUTH_API_ENDPOINTS.login}`, { email, password })
      .pipe(
        tap((response) => {
          this.tokenService.setTokens(response.data);
          this.isAuthenticated.set(true);
        }),
        switchMap((response) => this.onboardingService.checkOnboarding().pipe(map(() => response))),
      );
  }

  public subscribeToNewsLetter(): Observable<void> {
    return this.http.post<void>(
      `${this.fullApiUrl}${AUTH_API_ENDPOINTS.subscribeToNewsletter}`,
      {},
    );
  }

  public requestPasswordReset(email: string): Observable<unknown> {
    return this.http.post(`${this.fullApiUrl}${AUTH_API_ENDPOINTS.forgotPassword}`, { email });
  }

  public verifyResetToken(token: string): Observable<unknown> {
    return this.http.get(`${this.fullApiUrl}${AUTH_API_ENDPOINTS.validateResetToken}`, {
      params: { token },
    });
  }

  public resetPassword(token: string, newPassword: string): Observable<unknown> {
    const body: ResetPasswordRequest = { token, newPassword };

    return this.http.post(`${this.fullApiUrl}${AUTH_API_ENDPOINTS.resetPassword}`, body);
  }

  public validateNewPassword(token: string, newPassword: string): Observable<boolean> {
    const body: ResetPasswordRequest = { token, newPassword };

    return this.http
      .post<ValidateNewPasswordResponse>(
        `${this.fullApiUrl}${AUTH_API_ENDPOINTS.validateNewPassword}`,
        body,
      )
      .pipe(
        map(
          (response) =>
            response.matchesCurrentPassword ?? response.data?.matchesCurrentPassword ?? false,
        ),
      );
  }
}
